import * as React from 'react';
import { observer } from 'mobx-react';

import { PayerOnboardingUserAction } from '../../payer-onboarding-user-actions';
import { ExternalLink, RightChevronLink, DisabledRightChevronLink } from '../../../WebGiving/primitives/link/link';
import { isFunction } from '../../../Shared/utils/is-function';
import { Tooltip, TooltipPlacement } from '../../../WebGiving/components/tooltip/tooltip';
import { PayerOnboardingPushpayInfo } from '../../payer-onboarding-generated';
import { ModalHeader, ModalContents } from '../../../WebGiving/components/modal/modal';

import * as style from './help-modal.less';

export interface IHelpModalProps {
	showResendSecurityCode: boolean;
	pushpayInfo: PayerOnboardingPushpayInfo;
	resendCodeDisabled: boolean;
	onRequestClose?: () => void;
	raiseAction: (action: PayerOnboardingUserAction) => void;
}

export class HelpModalHeader extends React.Component {
	render() {
		return <ModalHeader text="Need help?" />;
	}
}

@observer
export class HelpModalContent extends React.Component<IHelpModalProps, {}> {
	public render() {
		const {
			showResendSecurityCode,
			resendCodeDisabled,
			pushpayInfo: { HelpCenterLink },
		} = this.props;

		const sendCode = showResendSecurityCode ? (
			<section className={style.section}>
				<h2 className={`${style.text} ${style.header}`}>Resend security code</h2>
				<div className={style.link}>
					<div className={style.linkItem}>
						<ResendCodeLink linkText={'Via text message'} onClick={this.resendTextMessage} disabled={resendCodeDisabled}/>
					</div>
					<div className={style.linkItem}>
						<ResendCodeLink linkText={'Via voice call'} onClick={this.resendVoiceCall} disabled={resendCodeDisabled}/>
					</div>
				</div>
			</section>
		) : (
				<section className={style.section}>
					<h2 className={`${style.text} ${style.header}`}>Receive a security code by voice call</h2>
					<div className={style.link}>
						<RightChevronLink branded={true} acceptanceTestTargetId="Send code via voice call" onClick={this.sendVoiceCall}>
							Send code via voice call
						</RightChevronLink>
					</div>
				</section>
			);

		return (
			<ModalContents>
				<p className="lead">
					We understand that sometimes there can be issues with receiving text messages. Please select an option from the list below if you are experiencing any issues.
				</p>

				{sendCode}

				<section className={style.section}>
					<div className={style.text}>
						<h2 className={style.header}>Still need help?</h2>
					</div>
					<div className={style.link}>
						<ExternalLink branded={true} acceptanceTestTargetId="Visit Help Center" href={HelpCenterLink}>
							Visit Help Center
						</ExternalLink>
					</div>
				</section>
			</ModalContents>
		);
	}

	private sendVoiceCall = () => {
		this.requestClose();
		this.props.raiseAction(new PayerOnboardingUserAction.RequestConfirmationCode(true));
	}

	private resendVoiceCall = () => {
		this.requestClose();
		this.props.raiseAction(new PayerOnboardingUserAction.RequestResendOfConfirmationCode(true));
	}

	private resendTextMessage = () => {
		this.requestClose();
		this.props.raiseAction(new PayerOnboardingUserAction.RequestResendOfConfirmationCode(false));
	}

	private requestClose() {
		if (isFunction(this.props.onRequestClose)) {
			this.props.onRequestClose();
		}
	}
}

interface ResendCodeLinkProps {
	linkText: string;
	disabled: boolean;
	onClick: () => void;
}

const ResendCodeLink: React.StatelessComponent<ResendCodeLinkProps> = (props) => {
	const {
		linkText,
		disabled,
		onClick,
	} = props;

	return (
		disabled
			? <Tooltip placement={TooltipPlacement.Bottom} content={`We can only send you a code every 20 seconds.`} hideOnMobile>
				<DisabledRightChevronLink branded={true}>{linkText}</DisabledRightChevronLink>
			</Tooltip>
			: <RightChevronLink branded={true} acceptanceTestTargetId={linkText} href="" disabled={disabled} onClick={onClick}>
				{linkText}
			</RightChevronLink>
	);
};
